<template>
  <nav class="navbar" role="navigation" aria-label="Main navigation">
    <div class="nav-content">
      <div
        class="nav-brand"
        @click="goToHome"
        role="button"
        tabindex="0"
        @keyup.enter="goToHome"
        aria-label="Go to homepage"
      >
        <img src="@/assets/logo.png" alt="" class="nav-logo" />
        <span class="brand-text">
          We <strong>Search</strong> Research
        </span>
      </div>

      <ul class="nav-links" v-if="!isAuthenticated">
        <li><a href="/how-it-works" class="nav-link">How it works</a></li>
        <li><RouterLink :to="ROUTES.ABOUT_US" class="nav-link">About us</RouterLink></li>
        <li><a href="/faq" class="nav-link">FAQ</a></li>
      </ul>

      <div class="nav-actions">
        <template v-if="!isAuthenticated">
          <button
            type="button"
            class="nav-btn nav-btn-primary"
            @click="startSearch"
            aria-label="Start my search"
          >
            Start my search
          </button>
          <button
            type="button"
            class="nav-btn nav-btn-outline"
            @click="goToResearcherLogin"
            aria-label="Researcher login"
          >
            Researcher Log in
          </button>
        </template>
        <template v-else>
          <span class="user-info" aria-live="polite">
            Welcome,
            <RouterLink
              :to="ROUTES.PROFILE"
              class="user-email user-email-link"
              :title="`View profile (${displayEmail})`"
              aria-label="View my profile"
            >{{ displayEmail }}</RouterLink>
          </span>
          <button
            type="button"
            class="nav-btn nav-btn-outline nav-btn-logout"
            @click="handleLogout"
            aria-label="Logout"
          >
            Logout
          </button>
        </template>
      </div>
    </div>

    <!-- Login Modal -->
    <LoginModal
      v-if="showLogin"
      :prefill-email="prefilledEmail"
      :info-message="loginInfoMessage"
      @close="handleCloseLogin"
      @login-success="handleLoginSuccess"
      @show-register="handleShowRegister"
      @show-forgot-password="handleShowForgotPassword"
    />

    <!-- Registration Modal -->
    <RegisterModal
      v-if="showRegister"
      @close="showRegister = false"
      @register-success="handleRegisterSuccess"
      @show-login="handleShowLogin"
    />

    <!-- Forgot Password Modal -->
    <ForgotPasswordModal
      v-if="showForgotPassword"
      @close="showForgotPassword = false"
      @show-login="handleShowLogin"
    />
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ROUTES } from '@/constants'
import LoginModal from '@/components/modals/LoginModal.vue'
import RegisterModal from '@/components/modals/RegisterModal.vue'
import ForgotPasswordModal from '@/components/modals/ForgotPasswordModal.vue'

export default {
  name: 'AppNavbar',
  components: {
    LoginModal,
    RegisterModal,
    ForgotPasswordModal
  },
  data() {
    return {
      ROUTES,
      showLogin: false,
      showRegister: false,
      showForgotPassword: false,
      prefilledEmail: '',
      loginInfoMessage: ''
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser', 'isAuthenticated']),
    displayEmail() {
      return this.currentUser?.email || 'User'
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    async handleLogout() {
      await this.logout()
      this.$router.push(ROUTES.HOME)
    },
    goToHome() {
      this.$router.push(ROUTES.HOME)
    },
    goToResearcherLogin() {
      this.$router.push(ROUTES.RESEARCHER_LOGIN)
    },
    startSearch() {
      if (this.isAuthenticated) {
        this.$router.push(ROUTES.CHAT)
      } else {
        this.showLogin = true
      }
    },
    handleLoginSuccess() {
      this.showLogin = false
      this.prefilledEmail = ''
      this.loginInfoMessage = ''
      this.$router.push(ROUTES.WELCOME)
    },
    handleRegisterSuccess(payload) {
      // Registration succeeded but the user is NOT logged in yet — the
      // backend requires OTP verification on first login. Hand off to the
      // LoginModal with the email pre-filled so they only need to enter
      // their password to trigger the OTP email.
      this.showRegister = false
      this.prefilledEmail = payload?.email || ''
      this.loginInfoMessage =
        'Account created. Please sign in to receive your verification code.'
      this.showLogin = true
    },
    handleCloseLogin() {
      this.showLogin = false
      this.prefilledEmail = ''
      this.loginInfoMessage = ''
    },
    handleShowRegister() {
      this.showLogin = false
      this.loginInfoMessage = ''
      this.showRegister = true
    },
    handleShowLogin() {
      this.showRegister = false
      this.showForgotPassword = false
      this.showLogin = true
    },
    handleShowForgotPassword() {
      this.showLogin = false
      this.showRegister = false
      this.showForgotPassword = true
    }
  }
}
</script>

<style scoped>
.navbar {
  background: #fef9f5;
  padding: 1.25rem 5rem;
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid rgba(10, 29, 20, 0.08);
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: opacity 0.15s ease;
  flex-shrink: 0;
}

.nav-brand:hover {
  opacity: 0.85;
}

.nav-brand:focus-visible {
  outline: 2px solid #0e3b2e;
  outline-offset: 3px;
  border-radius: 6px;
}

.nav-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.brand-text {
  font-size: 1.0625rem;
  color: #0f172a;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.brand-text strong {
  font-weight: 700;
}

.nav-links {
  list-style: none;
  display: flex;
  gap: 2.25rem;
  margin: 0;
  padding: 0;
  flex: 1;
  justify-content: center;
}

.nav-link {
  color: #0f172a;
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 500;
  transition: color 0.15s ease;
}

.nav-link:hover {
  color: #5b9d90;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.nav-btn {
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
  font-family: inherit;
  white-space: nowrap;
}

.nav-btn-primary {
  background: #10372f;
  color: #ffffff;
  border-color: #10372f;
  border-radius: 9999px;
  padding: 0.625rem 1rem;
}

.nav-btn-primary:hover {
  background: #0c2d24;
  border-color: #0c2d24;
}

.nav-btn-outline {
  background: transparent;
  color: #0a1d14;
  border-color: rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.nav-btn-outline:hover {
  background: rgba(0, 0, 0, 0.04);
  color: #0a1d14;
}

.user-info {
  color: #475569;
  font-size: 0.9375rem;
}

.user-email {
  font-weight: 600;
  color: #0f172a;
}

.user-email-link {
  text-decoration: none;
  border-bottom: 1px dashed transparent;
  transition: color 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}

.user-email-link:hover,
.user-email-link:focus-visible {
  color: #5b9d90;
  border-bottom-color: #5b9d90;
  outline: none;
}

@media (max-width: 900px) {
  .nav-links {
    display: none;
  }
}

@media (max-width: 640px) {
  .navbar {
    padding: 0.75rem 1rem;
  }

  .nav-content {
    gap: 0.75rem;
  }

  .brand-text {
    font-size: 0.95rem;
  }

  .nav-logo {
    width: 30px;
    height: 30px;
  }

  .nav-btn {
    padding: 0.45rem 0.8rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .nav-actions .nav-btn-outline:not(.nav-btn-logout) {
    display: none;
  }

  .user-info {
    display: none;
  }

  .nav-btn-logout {
    padding: 0.4rem 0.7rem;
    font-size: 0.8rem;
  }
}
</style>

